import React, { useEffect, useState, useRef } from 'react';
/**
 * FillInRenderer — Text/number input for open-ended questions.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
export function FillInRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const prevValidateRef = useRef(false);
  const config = question.data || {};
  const rawPlaceholder = config.placeholder || 'Typ je antwoord...';
  const inputType = config.inputType || 'text';
  // Determine if it's a currency/numeric question
  const hasEuroPlaceholder = rawPlaceholder.includes('€');
  // R7-DID1: Auto-detect numeric mode when all acceptedAnswers are numeric
  const hasNumericAnswers = (() => {
    const accepted: string[] = config.acceptedAnswers || [];
    if (accepted.length === 0) return false;
    return accepted.every((a: string) => {
      const normalized = String(a).replace(/[€\s,]/g, '.').trim();
      return normalized !== '' && !isNaN(parseFloat(normalized));
    });
  })();
  const isNumeric = inputType === 'number' || config.unit === '€' || hasEuroPlaceholder || hasNumericAnswers;
  // Determine unit and position
  let displayUnit = config.unit || '';
  let isPrefix = false;
  if (hasEuroPlaceholder && !displayUnit) {
    displayUnit = '€';
  }
  if (displayUnit === '€' || displayUnit === '$' || displayUnit === '£') {
    isPrefix = true;
  }
  // Clean up placeholder
  const placeholder = hasEuroPlaceholder && rawPlaceholder.length <= 4 ? '0,00' : rawPlaceholder.replace('€', '').trim();
  useEffect(() => {
    setValue('');
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(value.trim().length > 0);
  }, [value, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const answer = inputType === 'number' ? Number(value) : value;
      onAnswer(answer);
    }
  }, [shouldValidate, value, inputType, onAnswer]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (isNumeric) {
      // Allow only digits, comma, period, and minus sign
      val = val.replace(/[^0-9.,-]/g, '');
    }
    setValue(val);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-md mx-auto" data-id="element-8055">
      <motion.div animate={{
      scale: isFocused ? 1.02 : 1
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 20
    }} className="flex items-stretch bg-white border-2 rounded-2xl overflow-hidden transition-all shadow-sm" style={isFocused ? {
      borderColor: color,
      '--tw-ring-color': hexToRgba(color, 0.15),
      '--tw-ring-offset-width': '0px',
      '--tw-ring-offset-color': '#fff',
      '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
      '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
      boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000'
    } : {
      borderColor: '#e2e8f0' // slate-200
    }} data-id="element-8056">
        {displayUnit && isPrefix && <div className="flex items-center justify-center pl-5 pr-3 py-5 text-xl font-bold text-slate-500 bg-slate-50 border-r border-slate-200 select-none" data-id="element-8057">
            <LatexRenderer content={displayUnit} data-id="element-8058" />
          </div>}

        <input type={inputType === 'number' ? 'number' : 'text'} inputMode={isNumeric ? 'decimal' : 'text'} pattern={isNumeric ? '[0-9.,-]*' : undefined} value={value} onChange={handleChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder={placeholder} className="flex-1 p-5 text-xl font-medium text-slate-800 outline-none bg-transparent w-full" autoFocus data-id="element-8059" />

        {displayUnit && !isPrefix && <div className="flex items-center justify-center pr-5 pl-3 py-5 text-xl font-bold text-slate-500 bg-slate-50 border-l border-slate-200 select-none" data-id="element-8060">
            <LatexRenderer content={displayUnit} data-id="element-8061" />
          </div>}
      </motion.div>

      <p className="text-center text-slate-400 text-sm mt-6 font-medium" data-id="element-8062">
        {value.length > 0 ? 'Klaar? Klik op Controleren!' : 'Typ je antwoord in het vak hierboven'}
      </p>
    </motion.div>;
}